import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Autocomplete,
  InputAdornment,
  Switch,
  IconButton,
  Tooltip,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../config/api';
import {
  StyledTableContainer,
  TableSkeleton,
  EmptyState,
  StatusChip,
  PageLoader,
} from '../components/TableShared';
import StripeModeToggle from '../components/StripeModeToggle';

const ff = "'Poppins', sans-serif";

// Shared dashboard palette (see TableShared.js / Dashboard.js) so this page
// reads the same as every other screen.
const C = {
  heading: '#1A202C',
  body: '#2D3748',
  muted: '#718096',
  faint: '#A0AEC0',
  border: '#EDF2F7',
  surface: '#FAFBFC',
  accent: '#32A6D8',
  accentHover: '#2B8FB8',
  danger: '#E53E3E',
};

const formatPrice = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
const formatNumber = (num) => (num == null ? '0' : Number(num).toLocaleString());

const EMPTY_PACKAGE_FORM = {
  label: '',
  coins: '',
  bonusCoins: '0',
  priceInCents: '',
  sortOrder: '0',
  isActive: true,
};

const STAT_CONFIG = [
  {
    key: 'totalTransactions',
    label: 'Total Transactions',
    icon: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />,
    color: '#32A6D8',
  },
  {
    key: 'purchaseVolume',
    label: 'Purchase Volume',
    icon: <ShoppingCartIcon sx={{ fontSize: 20 }} />,
    color: '#38A169',
  },
  {
    key: 'spendVolume',
    label: 'Spend Volume',
    icon: <LocalOfferIcon sx={{ fontSize: 20 }} />,
    color: '#D69E2E',
  },
  {
    key: 'bonusVolume',
    label: 'Bonus Volume',
    icon: <CardGiftcardIcon sx={{ fontSize: 20 }} />,
    color: '#E53E3E',
  },
];

// ─── Stat Card (matches Dashboard.js StatCard visual) ───────────────
const StatCard = ({ label, value, icon, color }) => (
  <Box
    sx={{
      flex: '1 1 220px',
      minWidth: 200,
      p: 2.5,
      borderRadius: '14px',
      background: C.surface,
      border: `1px solid ${C.border}`,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        background: `${color}14`,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 500, color: C.faint, mb: 0.5 }}>
      {label}
    </Box>
    <Box sx={{ fontFamily: ff, fontSize: 24, fontWeight: 700, color: C.heading }}>
      {value}
    </Box>
  </Box>
);

// ─── Section Header (matches Dashboard.js SectionHeader) ────────────
const SectionHeader = ({ title, subtitle, action }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      mb: 2,
      flexWrap: 'wrap',
    }}
  >
    <Box>
      <Box sx={{ fontFamily: ff, fontSize: 16, fontWeight: 600, color: C.heading }}>
        {title}
      </Box>
      {subtitle && (
        <Box sx={{ fontFamily: ff, fontSize: 12.5, color: C.faint, mt: 0.25 }}>
          {subtitle}
        </Box>
      )}
    </Box>
    {action}
  </Box>
);

// ─── Shared primary-button style ────────────────────────────────────
// Matches the app-wide primary action button (#32A6D8), used on AppConfig,
// PayoutSettings, QRCodes, etc.
const primaryButtonSx = {
  fontFamily: ff,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '10px',
  background: C.accent,
  '&:hover': { background: C.accentHover },
};

const dialogFieldSx = { fontFamily: ff, borderRadius: '10px' };

export default function Coins() {
  const [stats, setStats] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Adjust dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ userId: '', amount: '', description: '' });
  const [adjustLoading, setAdjustLoading] = useState(false);

  // User autocomplete state
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Coin Packages state
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageForm, setPackageForm] = useState(EMPTY_PACKAGE_FORM);
  const [packageSaving, setPackageSaving] = useState(false);
  const [packageDeletingId, setPackageDeletingId] = useState(null);

  // Stripe mode — used here to show the LIVE banner contextually. The
  // toggle UI lives in <StripeModeToggle />; we just listen to its mode
  // changes via the onChange callback.
  const [stripeMode, setStripeModeLocal] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/coins/stats');
      const data = response.data.data || response.data;
      setStats({
        totalTransactions: data.totalTransactions || 0,
        purchaseVolume: data.purchaseVolume || 0,
        spendVolume: data.spendVolume || 0,
        bonusVolume: data.bonusVolume || 0,
      });
      setTopUsers(data.topUsers || []);
    } catch (err) {
      console.error('Error fetching coin stats:', err);
      setError('Failed to load coin statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStripeMode = useCallback(async () => {
    // Local cached copy used only to gate the LIVE banner. The toggle
    // component does its own fetch.
    try {
      const response = await api.get('/admin/stripe/mode');
      const data = response.data.data || response.data;
      setStripeModeLocal(data.mode || 'test');
    } catch (err) {
      console.error('Error fetching Stripe mode:', err);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      setPackagesLoading(true);
      const response = await api.get('/admin/coins/packages');
      const data = response.data.data || response.data;
      setPackages(data.packages || []);
    } catch (err) {
      console.error('Error fetching coin packages:', err);
      setSnackbar({ open: true, message: 'Failed to load coin packages.', severity: 'error' });
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPackages();
    fetchStripeMode();
  }, [fetchStats, fetchPackages, fetchStripeMode]);

  // Search users for autocomplete
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) {
      setUserOptions([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        setUserSearchLoading(true);
        const response = await api.get('/admin/users', {
          params: { search: userSearch, limit: 5 },
        });
        const data = response.data.data || response.data;
        setUserOptions(data.users || data || []);
      } catch (err) {
        console.error('Error searching users:', err);
        setUserOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [userSearch]);

  const handleOpenDialog = () => {
    setAdjustForm({ userId: '', amount: '', description: '' });
    setSelectedUser(null);
    setUserSearch('');
    setUserOptions([]);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAdjustSubmit = async () => {
    if (!selectedUser) {
      setSnackbar({ open: true, message: 'Please select a user.', severity: 'error' });
      return;
    }
    if (!adjustForm.amount || isNaN(Number(adjustForm.amount))) {
      setSnackbar({ open: true, message: 'Please enter a valid amount.', severity: 'error' });
      return;
    }
    try {
      setAdjustLoading(true);
      await api.post('/admin/coins/adjust', {
        userId: selectedUser.id || selectedUser._id,
        amount: Number(adjustForm.amount),
        description: adjustForm.description,
      });
      setSnackbar({ open: true, message: 'Coins adjusted successfully.', severity: 'success' });
      setDialogOpen(false);
      fetchStats();
    } catch (err) {
      console.error('Error adjusting coins:', err);
      const message = err.response?.data?.message || 'Failed to adjust coins. Please try again.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setAdjustLoading(false);
    }
  };

  // ─── Coin Packages handlers ─────────────────────────────────────────────

  const handleOpenAddPackage = () => {
    setEditingPackageId(null);
    setPackageForm(EMPTY_PACKAGE_FORM);
    setPackageDialogOpen(true);
  };

  const handleOpenEditPackage = (pkg) => {
    setEditingPackageId(pkg.id);
    setPackageForm({
      label: pkg.label || '',
      coins: String(pkg.coins ?? ''),
      bonusCoins: String(pkg.bonusCoins ?? 0),
      priceInCents: String(pkg.priceInCents ?? ''),
      sortOrder: String(pkg.sortOrder ?? 0),
      isActive: !!pkg.isActive,
    });
    setPackageDialogOpen(true);
  };

  const handleClosePackageDialog = () => {
    if (packageSaving) return;
    setPackageDialogOpen(false);
  };

  const handleSavePackage = async () => {
    // Validate before hitting the API so we don't pay a round-trip for
    // obvious mistakes. Backend re-validates everything anyway.
    const label = packageForm.label.trim();
    const coins = parseInt(packageForm.coins, 10);
    const bonusCoins = parseInt(packageForm.bonusCoins || '0', 10);
    const priceInCents = parseInt(packageForm.priceInCents, 10);
    const sortOrder = parseInt(packageForm.sortOrder || '0', 10);

    if (!label) return setSnackbar({ open: true, message: 'Label is required.', severity: 'error' });
    if (!Number.isInteger(coins) || coins < 1) return setSnackbar({ open: true, message: 'Coins must be a positive integer.', severity: 'error' });
    if (!Number.isInteger(bonusCoins) || bonusCoins < 0) return setSnackbar({ open: true, message: 'Bonus coins must be 0 or more.', severity: 'error' });
    if (!Number.isInteger(priceInCents) || priceInCents < 1) return setSnackbar({ open: true, message: 'Price (cents) must be a positive integer.', severity: 'error' });
    if (!Number.isInteger(sortOrder)) return setSnackbar({ open: true, message: 'Sort order must be an integer.', severity: 'error' });

    const payload = { label, coins, bonusCoins, priceInCents, sortOrder, isActive: !!packageForm.isActive };
    try {
      setPackageSaving(true);
      if (editingPackageId) {
        await api.put(`/admin/coins/packages/${editingPackageId}`, payload);
        setSnackbar({ open: true, message: 'Package updated.', severity: 'success' });
      } else {
        await api.post('/admin/coins/packages', payload);
        setSnackbar({ open: true, message: 'Package created.', severity: 'success' });
      }
      setPackageDialogOpen(false);
      fetchPackages();
    } catch (err) {
      console.error('Error saving coin package:', err);
      const message = err.response?.data?.message || 'Failed to save package.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setPackageSaving(false);
    }
  };

  const handleTogglePackageActive = async (pkg) => {
    try {
      await api.put(`/admin/coins/packages/${pkg.id}`, { isActive: !pkg.isActive });
      setSnackbar({
        open: true,
        message: `Package ${!pkg.isActive ? 'activated' : 'deactivated'}.`,
        severity: 'success',
      });
      fetchPackages();
    } catch (err) {
      console.error('Error toggling package:', err);
      setSnackbar({ open: true, message: 'Failed to toggle package.', severity: 'error' });
    }
  };

  // Soft-delete confirms with a window.confirm because the action is reversible
  // (admin can reactivate from the same table). For destructive irreversible
  // actions we'd use a real ConfirmDialog.
  const handleDeletePackage = async (pkg) => {
    const ok = window.confirm(`Deactivate "${pkg.label}"? It will be hidden from the Shop but can be reactivated later.`);
    if (!ok) return;
    try {
      setPackageDeletingId(pkg.id);
      await api.delete(`/admin/coins/packages/${pkg.id}`);
      setSnackbar({ open: true, message: 'Package deactivated.', severity: 'success' });
      fetchPackages();
    } catch (err) {
      console.error('Error deleting package:', err);
      setSnackbar({ open: true, message: 'Failed to deactivate package.', severity: 'error' });
    } finally {
      setPackageDeletingId(null);
    }
  };

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: C.accent },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.accent },
  };

  return (
    <Box sx={{ fontFamily: ff }}>
      {/* Action row — the page title/subtitle is rendered by <Layout />, so we
          only surface the controls here (no duplicate heading). alignItems is
          flex-start so the toggle's inline warning (which stacks below it) can't
          shove the Adjust Coins button around. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {/* Full-variant toggle. Shows missing-key warnings inline. */}
        <StripeModeToggle
          variant="full"
          onChange={(nextMode) => {
            setStripeModeLocal(nextMode);
            setSnackbar({
              open: true,
              message: `Stripe is now in ${nextMode.toUpperCase()} mode.`,
              severity: nextMode === 'live' ? 'warning' : 'success',
            });
          }}
        />

        <Button
          variant="contained"
          startIcon={<TuneIcon />}
          onClick={handleOpenDialog}
          sx={{ ...primaryButtonSx, px: 3, py: 1.1 }}
        >
          Adjust Coins
        </Button>
      </Box>

      {/* LIVE mode banner — the toggle component handles missing-key warnings
          itself; this one is just the loud reminder when LIVE is active. */}
      {stripeMode === 'live' && (
        <Alert severity="error" sx={{ mb: 2.5, fontFamily: ff, fontWeight: 500, borderRadius: '10px' }}>
          <strong>Stripe is in LIVE mode.</strong> Real cards will be charged on every purchase. Use TEST mode for development.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <PageLoader />
      ) : (
        <>
          {/* Stat Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {STAT_CONFIG.map((cfg) => (
              <StatCard
                key={cfg.key}
                label={cfg.label}
                value={formatNumber(stats?.[cfg.key])}
                icon={cfg.icon}
                color={cfg.color}
              />
            ))}
          </Box>

          {/* Coin Packages section */}
          <Box sx={{ mb: 4 }}>
            <SectionHeader
              title="Coin Packages"
              subtitle="Packages shown to users in the Shop Coins screen"
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddPackage}
                  sx={{ ...primaryButtonSx, px: 2.5, py: 0.9 }}
                >
                  Add Package
                </Button>
              }
            />

            {packagesLoading ? (
              <TableSkeleton columns={8} rows={4} />
            ) : (
              <StyledTableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Coins</TableCell>
                      <TableCell align="right">Bonus</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Sort</TableCell>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packages.length > 0 ? (
                      packages.map((pkg) => (
                        <TableRow key={pkg.id} sx={{ opacity: pkg.isActive ? 1 : 0.55 }}>
                          <TableCell sx={{ fontWeight: 600, color: C.body }}>
                            {pkg.label}
                          </TableCell>
                          <TableCell align="right">{formatNumber(pkg.coins)}</TableCell>
                          <TableCell align="right" sx={{ color: '#276749', fontWeight: 600 }}>
                            {pkg.bonusCoins > 0 ? `+${formatNumber(pkg.bonusCoins)}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: C.body }}>
                            {formatNumber((pkg.coins || 0) + (pkg.bonusCoins || 0))}
                          </TableCell>
                          <TableCell align="right">{formatPrice(pkg.priceInCents)}</TableCell>
                          <TableCell align="right" sx={{ color: C.faint }}>
                            {pkg.sortOrder}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={!!pkg.isActive}
                              onChange={() => handleTogglePackageActive(pkg)}
                              size="small"
                              sx={switchSx}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditPackage(pkg)}
                                sx={{ color: C.accent }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deactivate">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!pkg.isActive || packageDeletingId === pkg.id}
                                  onClick={() => handleDeletePackage(pkg)}
                                  sx={{ color: C.danger }}
                                >
                                  {packageDeletingId === pkg.id ? (
                                    <CircularProgress size={16} sx={{ color: C.danger }} />
                                  ) : (
                                    <DeleteOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <EmptyState
                        colSpan={8}
                        message="No packages yet. Add one to make it available in the Shop."
                      />
                    )}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}
          </Box>

          {/* Top Users section */}
          <Box>
            <SectionHeader
              title="Top Users by Coin Balance"
              subtitle="Top 10 users with the highest coin balances"
            />
            <StyledTableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topUsers.length > 0 ? (
                    topUsers.map((user, index) => (
                      <TableRow key={user.id || user._id || index}>
                        <TableCell sx={{ color: C.faint }}>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: C.body }}>
                          {user.fullName || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: C.muted }}>{user.email || 'N/A'}</TableCell>
                        <TableCell align="right">
                          <StatusChip
                            label={formatNumber(user.coinBalance)}
                            color="#276749"
                            bgColor="#F0FFF4"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyState colSpan={4} message="No users found." />
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Box>
        </>
      )}

      {/* Adjust Coins Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, color: C.heading }}>
          Adjust User Coins
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: C.faint, mb: 3 }}>
            Add (positive number) or deduct (negative number) coins from a user's balance.
          </Box>

          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) =>
              option.fullName
                ? `${option.fullName} (${option.email})`
                : option.email || ''
            }
            loading={userSearchLoading}
            value={selectedUser}
            onChange={(e, newValue) => {
              setSelectedUser(newValue);
              setAdjustForm((prev) => ({
                ...prev,
                userId: newValue ? newValue.id || newValue._id : '',
              }));
            }}
            onInputChange={(e, newInput) => {
              setUserSearch(newInput);
            }}
            isOptionEqualToValue={(option, value) =>
              (option.id || option._id) === (value.id || value._id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search User by Email or Name"
                placeholder="Type at least 2 characters..."
                fullWidth
                sx={{ mb: 2.5 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {userSearchLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  sx: dialogFieldSx,
                }}
                InputLabelProps={{ sx: { fontFamily: ff } }}
              />
            )}
            noOptionsText={userSearch.length < 2 ? 'Type to search...' : 'No users found'}
          />

          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={adjustForm.amount}
            onChange={(e) =>
              setAdjustForm((prev) => ({ ...prev, amount: e.target.value }))
            }
            placeholder="e.g. 100 to add, -50 to deduct"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountBalanceWalletIcon sx={{ color: C.faint, fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: dialogFieldSx,
            }}
            InputLabelProps={{ sx: { fontFamily: ff } }}
            sx={{ mb: 2.5 }}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={adjustForm.description}
            onChange={(e) =>
              setAdjustForm((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Reason for adjustment..."
            InputProps={{ sx: dialogFieldSx }}
            InputLabelProps={{ sx: { fontFamily: ff } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ fontFamily: ff, textTransform: 'none', color: C.muted, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdjustSubmit}
            disabled={adjustLoading || !selectedUser || !adjustForm.amount}
            sx={{ ...primaryButtonSx, px: 3 }}
          >
            {adjustLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coin Package Add/Edit Dialog */}
      <Dialog
        open={packageDialogOpen}
        onClose={handleClosePackageDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: ff, fontWeight: 600, color: C.heading }}>
          {editingPackageId ? 'Edit Coin Package' : 'Add Coin Package'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: ff, fontSize: 13, color: C.faint, mb: 3 }}>
            Bonus coins are credited along with the base coins on purchase and cannot be cashed out.
          </Box>

          <TextField
            label="Label"
            fullWidth
            value={packageForm.label}
            onChange={(e) => setPackageForm((p) => ({ ...p, label: e.target.value }))}
            placeholder="e.g. 1,000 Coins"
            InputProps={{ sx: dialogFieldSx }}
            InputLabelProps={{ sx: { fontFamily: ff } }}
            sx={{ mb: 2.5 }}
          />

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6}>
              <TextField
                label="Coins"
                type="number"
                fullWidth
                value={packageForm.coins}
                onChange={(e) => setPackageForm((p) => ({ ...p, coins: e.target.value }))}
                placeholder="1000"
                InputProps={{ sx: dialogFieldSx }}
                InputLabelProps={{ sx: { fontFamily: ff } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Bonus Coins"
                type="number"
                fullWidth
                value={packageForm.bonusCoins}
                onChange={(e) => setPackageForm((p) => ({ ...p, bonusCoins: e.target.value }))}
                placeholder="0"
                InputProps={{ sx: dialogFieldSx }}
                InputLabelProps={{ sx: { fontFamily: ff } }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={6}>
              <TextField
                label="Price (cents)"
                type="number"
                fullWidth
                value={packageForm.priceInCents}
                onChange={(e) => setPackageForm((p) => ({ ...p, priceInCents: e.target.value }))}
                placeholder="4499"
                helperText={
                  packageForm.priceInCents
                    ? `= ${formatPrice(packageForm.priceInCents)}`
                    : 'e.g. 4499 for $44.99'
                }
                InputProps={{ sx: dialogFieldSx }}
                InputLabelProps={{ sx: { fontFamily: ff } }}
                FormHelperTextProps={{ sx: { fontFamily: ff, fontSize: 11 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sort Order"
                type="number"
                fullWidth
                value={packageForm.sortOrder}
                onChange={(e) => setPackageForm((p) => ({ ...p, sortOrder: e.target.value }))}
                placeholder="0"
                helperText="Lower numbers appear first"
                InputProps={{ sx: dialogFieldSx }}
                InputLabelProps={{ sx: { fontFamily: ff } }}
                FormHelperTextProps={{ sx: { fontFamily: ff, fontSize: 11 } }}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderRadius: '10px',
              border: `1px solid ${C.border}`,
            }}
          >
            <Box>
              <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 500, color: C.body }}>
                Active
              </Box>
              <Box sx={{ fontFamily: ff, fontSize: 12, color: C.faint }}>
                Inactive packages are hidden from the Shop screen
              </Box>
            </Box>
            <Switch
              checked={!!packageForm.isActive}
              onChange={(e) => setPackageForm((p) => ({ ...p, isActive: e.target.checked }))}
              sx={switchSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleClosePackageDialog}
            disabled={packageSaving}
            sx={{ fontFamily: ff, textTransform: 'none', color: C.muted, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePackage}
            disabled={packageSaving}
            sx={{ ...primaryButtonSx, px: 3 }}
          >
            {packageSaving ? (
              <CircularProgress size={22} sx={{ color: '#fff' }} />
            ) : editingPackageId ? (
              'Save Changes'
            ) : (
              'Create Package'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ fontFamily: ff, borderRadius: '10px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
